"use client"

import { useState, useEffect } from "react"
import { X, Briefcase, DollarSign, Calendar, FileText } from "lucide-react"
import axios from "axios"
import config from "../config"

const ServiceRequestModal = ({ isOpen, onClose, event, fetchServiceRequests, showSuccessModal, showFailureModal }) => {
  const [bidDetails, setBidDetails] = useState({
    title: "",
    description: "",
    budget: "",
    deadline: "",
    status: "pending",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (event?.date && !bidDetails.deadline) {
      const formattedEventDate = new Date(event.date).toISOString().split("T")[0]
      setBidDetails((prev) => ({
        ...prev,
        deadline: formattedEventDate,
      }))
    }
  }, [event?.date, bidDetails.deadline])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const token = localStorage.getItem("access_token")
      if (!token) {
        showFailureModal("Authentication token missing. Please log in again.")
        return
      }

      const requestData = {
        event: event.id,
        title: bidDetails.title,
        description: bidDetails.description,
        budget: bidDetails.budget,
        deadline: bidDetails.deadline,
        status: bidDetails.status || "pending",
      }

      const response = await axios.post(`${config.API_BASE_URL}/bids/create/`, requestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 201) {
        showSuccessModal("Service request created successfully!")
        setBidDetails({ title: "", description: "", budget: "", deadline: "", status: "pending" })
        onClose()
        await fetchServiceRequests()
      }
    } catch (error) {
      // console.error("Error submitting service request:", error)
      showFailureModal(error.response?.data?.error || "Failed to create service request.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Briefcase className="text-purple-600" size={20} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Request a Service</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors duration-150">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Title</label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="e.g., Photography, Catering, DJ Services"
                value={bidDetails.title}
                onChange={(e) => setBidDetails({ ...bidDetails, title: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              placeholder="Describe what you need in detail..."
              value={bidDetails.description}
              onChange={(e) => setBidDetails({ ...bidDetails, description: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={4}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Budget</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <span className="absolute left-8 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">KSh</span>
              <input
                type="number"
                placeholder="0"
                value={bidDetails.budget}
                onChange={(e) => setBidDetails({ ...bidDetails, budget: e.target.value })}
                className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="date"
                value={bidDetails.deadline}
                onChange={(e) => setBidDetails({ ...bidDetails, deadline: e.target.value })}
                min={new Date(event?.date).toISOString().split("T")[0]}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Service must be completed by this date</p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-lg transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Creating..." : "Create Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ServiceRequestModal
